/**
 * Ribbon rack API (docs/Decisions.md D42; integration/openapi.yaml, the
 * /member-badges/ribbons, /member-badges/rack, /member-badges/milestones and
 * /member-badges/progress paths). The active org travels in the X-Org-Id
 * header that lib/apiClient.js adds to every request.
 */
import { get, post, put } from '../lib/apiClient';

/** GET /member-badges/ribbons/catalog - pre-built ribbons and the design vocabulary */
export const getRibbonCatalog = () => get('/member-badges/ribbons/catalog');

/** GET /member-badges/ribbons/definitions - the active org's designs and its premium features */
export const listDefinitions = () => get('/member-badges/ribbons/definitions');

/** POST /member-badges/ribbons/definitions - create a design (org admin, premium) */
export const createDefinition = definition =>
  post('/member-badges/ribbons/definitions', definition);

/** PUT /member-badges/ribbons/definitions/{ribbonId} - edit or archive a design */
export const updateDefinition = (ribbonId, changes) =>
  put(`/member-badges/ribbons/definitions/${encodeURIComponent(ribbonId)}`, changes);

/** POST /member-badges/ribbons/award - award an org design (org staff, premium) */
export const awardRibbon = ({ userId, ribbonId, levelIndex, reason }) =>
  post('/member-badges/ribbons/award', { userId, ribbonId, levelIndex, reason });

/** POST /member-badges/ribbons/level - set an award's achievement level */
export const setRibbonLevel = ({ userId, ribbonId, levelIndex, reason }) =>
  post('/member-badges/ribbons/level', { userId, ribbonId, levelIndex, reason });

/** POST /member-badges/ribbons/revoke - revoke an award */
export const revokeRibbon = ({ userId, ribbonId, reason }) =>
  post('/member-badges/ribbons/revoke', { userId, ribbonId, reason });

/** POST /member-badges/milestones - staff record an investment made */
export const recordMilestone = ({ userId, metric, note, reference }) =>
  post('/member-badges/milestones', { userId, metric, note, reference });

/** GET /member-badges/rack/me - the caller's rack across every org */
export const getMyRack = () => get('/member-badges/rack/me');

/** GET /member-badges/rack/{userId} - another member's rack, visibility enforced */
export const getUserRack = userId => get(`/member-badges/rack/${encodeURIComponent(userId)}`);

/** GET the rack for `userId`, where 'me' (or nothing) means the caller. */
export const getRack = userId => (!userId || userId === 'me' ? getMyRack() : getUserRack(userId));

/** PUT /member-badges/rack/me - arrange (order, hide) the caller's rack in the active org */
export const arrangeMyRack = ({ order, hidden }) =>
  put('/member-badges/rack/me', { order, hidden });

/** GET /member-badges/rack/layout - the active org's rack layout */
export const getRackLayout = () => get('/member-badges/rack/layout');

/** PUT /member-badges/rack/layout - org admins set precedence and layout */
export const putRackLayout = ({ precedence, ribbonsPerRow, showLevels }) =>
  put('/member-badges/rack/layout', { precedence, ribbonsPerRow, showLevels });

/** GET /member-badges/progress/me - counters and progress toward each pre-built ribbon */
export const getMyProgress = () => get('/member-badges/progress/me');
